import React, { useState } from "react";
import { makeStyles } from "@mui/styles";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import round from "lodash/round";

const useStyles = makeStyles({
    pageContainer: {
        width: "25%",
        overflowY: "auto",
        padding: "20px",
        backgroundColor: "#ededed",
        borderTopLeftRadius: "15px",
        borderBottomLeftRadius: "15px",
        borderRight: "5px solid #757575",
    },
    listHeader: {
        textAlign: "center",
        fontWeight: "bold",
        marginBottom: "10px",
        borderBottom: "2px solid #757575",
        paddingBottom: "5px",
    },
    spansLabel: {
        fontWeight: "bold",
        paddingLeft: "16px",
        textAlign: "center",
        marginTop: "10px",
        borderBottom: "1px solid #ccc",
        paddingBottom: "5px",
    },
    spanBullet: {
        textAlign: "center",
    },
    spanDetails: {
        fontSize: "0.85em",
        textAlign: "center",
        padding: "5px 10px",
        backgroundColor: "#f5f5f5",
        borderRadius: "5px",
        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
        color: "#333",
        fontFamily: "'Roboto', sans-serif",
        margin: "3px 0",
    },
    iconStyle: {
        paddingRight: "10px",
        color: "#757575",
    },
});

// Extract span IDs from validation data entries (e.g. "1-2 (NV Energy ...)" -> "1-2")
const getFailedSpanNames = (failedSpans) => {
    if (!failedSpans) return new Set();
    const allEntries = [
        ...(failedSpans.high_coefficients || []),
        ...(failedSpans.low_coefficients || []),
        ...(failedSpans.high_angle_differences || []),
    ];
    return new Set(allEntries.map((entry) => entry.split(" (")[0]));
};

export default function SimulationAreaDropdown({
    type,
    simulationAreas,
    simulationSpans,
    weatherSensors,
    coefficients,
    onSpanSelect,
    onWeatherSensorSelect,
    onAreaSelect,
    failedSpans,
}) {
    const classes = useStyles();
    const failedSpanNames = getFailedSpanNames(failedSpans);
    const [expandedAreas, setExpandedAreas] = useState([]);
    const [selectedSpan, setSelectedSpan] = useState(null);

    const toggleExpandArea = (uuid) => {
        if (expandedAreas.includes(uuid)) {
            setExpandedAreas((prevState) => prevState.filter((id) => id !== uuid));
        } else {
            setExpandedAreas((prevState) => [...prevState, uuid]);
        }
    };

    const handleSpanClick = (spanName) => {
        if (selectedSpan && selectedSpan.name == spanName) {
            setSelectedSpan(null);
            return;
        }

        onSpanSelect(spanName);
        const spanDetails = simulationSpans.spans.find((span) => span.name === spanName);
        setSelectedSpan(spanDetails);
    };

    const getAreaId = (area) => {
        return area.id;
    };

    return (
        <div className={classes.pageContainer}>
            <List component="nav" aria-label="simulation areas">
                <Typography variant="h6" gutterBottom className={classes.listHeader}>
                    Simulation Areas
                </Typography>
                {simulationAreas.simulation_areas.map((area) => {
                    const areaId = getAreaId(area);
                    const areaHasFailedSpan = area.spans?.some((s) => failedSpanNames.has(s.split(" (")[0]));
                    return (
                        <div key={areaId}>
                            <ListItemButton
                                onClick={() => {
                                    toggleExpandArea(areaId);
                                    onAreaSelect(area);
                                }}
                                sx={areaHasFailedSpan ? { backgroundColor: "#fff3e0" } : undefined}
                            >
                                <ListItemText
                                    primary={area.group_name}
                                    sx={areaHasFailedSpan ? { "& .MuiTypography-root": { color: "#e65100", fontWeight: "bold" } } : undefined}
                                />
                                {expandedAreas.includes(areaId) ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                            <Collapse in={expandedAreas.includes(areaId)} timeout="auto" unmountOnExit>
                                <Typography variant="subtitle1" className={classes.spansLabel}></Typography>

                                {type === "cad" ? (
                                    <CadDetails
                                        classes={classes}
                                        area={area}
                                        selectedSpan={selectedSpan}
                                        handleSpanClick={handleSpanClick}
                                    />
                                ) : type === "span" ? (
                                    <SpanDetails
                                        coefficients={coefficients}
                                        classes={classes}
                                        area={area}
                                        selectedSpan={selectedSpan}
                                        handleSpanClick={handleSpanClick}
                                        failedSpanNames={failedSpanNames}
                                    />
                                ) : type === "weatherSensor" ? (
                                    <WeatherSensorDetails
                                        weatherSensors={weatherSensors}
                                        coefficients={coefficients}
                                        classes={classes}
                                        area={area}
                                        onWeatherSensorSelect={onWeatherSensorSelect}
                                    />
                                ) : null}
                            </Collapse>
                        </div>
                    );
                })}
            </List>
        </div>
    );
}

function CadDetails({ classes, area, selectedSpan, handleSpanClick }) {
    return (
        <>
            {area.spans.map((spanName) => (
                <div key={spanName}>
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => handleSpanClick(spanName)}>
                            <ListItemText primary={spanName} className={classes.spanBullet} />
                        </ListItemButton>
                    </ListItem>
                    {selectedSpan && selectedSpan.name === spanName && (
                        <>
                            <Collapse in={true} timeout={{ enter: 500, exit: 500 }} unmountOnExit>
                                <List component="div" disablePadding>
                                    <ListItem>
                                        <InfoIcon className={classes.iconStyle} />
                                        <ListItemText
                                            primary={`Start Structure Location: ${selectedSpan.start_structure.latitude}°, ${selectedSpan.start_structure.longitude}°`}
                                            primaryTypographyProps={{
                                                className: classes.spanDetails,
                                            }}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <InfoIcon className={classes.iconStyle} />
                                        <ListItemText
                                            primary={`Start Conductor Elevation: ${round(
                                                +selectedSpan.start_structure.min_cond_elevation,
                                                3
                                            )} (m)`}
                                            primaryTypographyProps={{
                                                className: classes.spanDetails,
                                            }}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <InfoIcon className={classes.iconStyle} />
                                        <ListItemText
                                            primary={`Start Conductor HAG: ${round(
                                                +selectedSpan.start_structure.min_cond_hag,
                                                3
                                            )} (m)`}
                                            primaryTypographyProps={{
                                                className: classes.spanDetails,
                                            }}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <InfoIcon className={classes.iconStyle} />
                                        <ListItemText
                                            primary={`End Structure Location: ${selectedSpan.end_structure.latitude}°, ${selectedSpan.end_structure.longitude}°`}
                                            primaryTypographyProps={{
                                                className: classes.spanDetails,
                                            }}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <InfoIcon className={classes.iconStyle} />
                                        <ListItemText
                                            primary={`End Conductor Elevation: ${round(
                                                +selectedSpan.end_structure.min_cond_elevation,
                                                3
                                            )} (m)`}
                                            primaryTypographyProps={{
                                                className: classes.spanDetails,
                                            }}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <InfoIcon className={classes.iconStyle} />
                                        <ListItemText
                                            primary={`End Conductor HAG: ${round(
                                                +selectedSpan.end_structure.min_cond_hag,
                                                3
                                            )} (m)`}
                                            primaryTypographyProps={{
                                                className: classes.spanDetails,
                                            }}
                                        />
                                    </ListItem>

                                    <ListItem>
                                        <InfoIcon className={classes.iconStyle} />
                                        <ListItemText
                                            primary={`Midspan Elevation: ${round(
                                                +selectedSpan.mid_span_min_cond_elevation,
                                                3
                                            )} (m)`}
                                            primaryTypographyProps={{
                                                className: classes.spanDetails,
                                            }}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <InfoIcon className={classes.iconStyle} />
                                        <ListItemText
                                            primary={`Midspan HAG: ${round(
                                                +selectedSpan.mid_span_min_cond_hag,
                                                3
                                            )} (m)`}
                                            primaryTypographyProps={{
                                                className: classes.spanDetails,
                                            }}
                                        />
                                    </ListItem>
                                </List>
                            </Collapse>
                        </>
                    )}
                </div>
            ))}
        </>
    );
}

function SpanDetails({ coefficients, classes, area, selectedSpan, handleSpanClick, failedSpanNames }) {
    return (
        <>
            {area.spans.map((spanName) => {
                const isFailed = failedSpanNames && failedSpanNames.has(spanName.split(" (")[0]);
                return (
                <div key={spanName}>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => handleSpanClick(spanName)}
                            sx={isFailed ? { backgroundColor: "#fff3e0" } : undefined}
                        >
                            <ListItemText
                                primary={spanName}
                                className={classes.spanBullet}
                                sx={isFailed ? { "& .MuiTypography-root": { color: "#e65100", fontWeight: "bold" } } : undefined}
                            />
                        </ListItemButton>
                    </ListItem>
                    {selectedSpan && selectedSpan.name === spanName && <CoefficientTable coefficients={coefficients} />}
                </div>
                );
            })}
        </>
    );
}

function WeatherSensorDetails({ weatherSensors, coefficients, classes, area, onWeatherSensorSelect }) {
    const [selectedWeatherSensorSn, setSelectedWeatherSensorSn] = useState(null);

    function handleWeatherSensorClick(weatherSensorSn) {
        if (selectedWeatherSensorSn == weatherSensorSn) {
            setSelectedWeatherSensorSn(null);
            return;
        }

        onWeatherSensorSelect(weatherSensorSn);
        setSelectedWeatherSensorSn(weatherSensorSn);
    }

    const { max_latitude, min_latitude, max_longitude, min_longitude } = area.bbox;
    const areaWeatherSensors = (weatherSensors || []).filter(
        (ws) =>
            +ws.latitude >= +min_latitude &&
            +ws.latitude <= +max_latitude &&
            +ws.longitude >= +min_longitude &&
            +ws.longitude <= +max_longitude
    );

    if (areaWeatherSensors.length === 0) {
        return <Typography variant="body2">No weather sensors available</Typography>;
    }

    return (
        <>
            {areaWeatherSensors.map((weatherSensor) => (
                <div key={weatherSensor.serialNo}>
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => handleWeatherSensorClick(weatherSensor.serialNo)}>
                            <ListItemText primary={weatherSensor.serialNo} className={classes.spanBullet} />
                        </ListItemButton>
                    </ListItem>
                    {selectedWeatherSensorSn && weatherSensor.serialNo === selectedWeatherSensorSn && (
                        <CoefficientTable coefficients={coefficients} />
                    )}
                </div>
            ))}
        </>
    );
}

function CoefficientTable({ coefficients }) {
    return (
        <>
            <Collapse in={true} timeout="auto" unmountOnExit>
                <TableContainer component={Paper}>
                    <Table aria-label="coefficient table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Wind Direction</TableCell>
                                <TableCell align="right">Coefficients</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(coefficients.coefficients).map(([direction, coeffValue]) => (
                                <TableRow key={direction}>
                                    <TableCell component="th" scope="row">
                                        {direction}
                                    </TableCell>
                                    <TableCell align="right">{coeffValue}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Collapse>
        </>
    );
}
